import { useCallback } from 'react';

import { VisitAndAppointmentStatus } from '@dreryk/egabinet-visit-frontend-api';

import {
   ButtonVariant,
   Modal,
   ModalCloseButton,
   ModalConfirmButton,
   ModalMaxHeight,
   ModalWidth,
   StyledFlex,
   useHistoryPushWithBackAction,
} from '@dreryk/react-components';

import { visitPaths } from '~libs.desktop.shared.paths';

import { PreInterviewPreviewItem } from '../../../common/preInterview/PreInterviewPreviewItem';
import { PreInterviewPreviewItemsLoader } from '../../../common/preInterview/PreInterviewPreviewItems.loader';
import { appointmentsTranslationKeys, historicalVisitTranslationKeys } from '../../../translations';
import { PatientInformationView } from '../shared/PatientInformationView/PatientInformationView';

type PreInterviewPreviewModalProps = {
   readonly appointmentId: number;
   readonly beginDate: Date;
   readonly endDate: Date;
   readonly patientFullName: string;
   readonly patientId: number;
   readonly onClose: VoidFunction;
   readonly visitId: number | undefined;
   readonly visitStatus: VisitAndAppointmentStatus;
};

export const PreInterviewPreviewModal = (props: PreInterviewPreviewModalProps): JSX.Element => {
   const push = useHistoryPushWithBackAction(true);

   const handleClick = useCallback(() => {
      push(
         visitPaths.getVisitHistoryPath({
            params: { patientId: props.patientId },
            searchParams: { visitId: props.visitId },
         }),
         { label: historicalVisitTranslationKeys.goBackToList }
      );
   }, [push, props.patientId, props.visitId]);

   return (
      <Modal
         testId={'preInterviewPreview'}
         title={appointmentsTranslationKeys.preInterview.modal.shared.title}
         width={ModalWidth.WIDE}
         height={ModalMaxHeight.TALL}
      >
         <StyledFlex direction={'column'} gap={24}>
            <PatientInformationView
               beginDate={props.beginDate}
               endDate={props.endDate}
               patientFullName={props.patientFullName}
            />
            <PreInterviewPreviewItemsLoader appointmentId={props.appointmentId}>
               {({ preInterviewItems }) => (
                  <>
                     {preInterviewItems.map((item) => (
                        <PreInterviewPreviewItem
                           key={item.id}
                           questionContent={item.questionContent}
                           questionNumber={item.questionNumber}
                           answerContent={item.answerContent}
                        />
                     ))}
                  </>
               )}
            </PreInterviewPreviewItemsLoader>
         </StyledFlex>

         <ModalConfirmButton
            onClick={handleClick}
            testId={'previewVisit'}
            label={appointmentsTranslationKeys.preInterview.modal.preview.actions.previewVisit}
            variant={ButtonVariant.SECONDARY}
            disabled={props.visitStatus !== 'FINISHED_APPROVED'}
         />
         <ModalConfirmButton
            onClick={props.onClose}
            testId={'close'}
            label={appointmentsTranslationKeys.preInterview.modal.preview.actions.close}
         />
         <ModalCloseButton onClick={props.onClose} />
      </Modal>
   );
};
